import PropTypes from 'prop-types'
import React, { useRef, useState, useEffect } from 'react'
import type { DnDProps, FileProps, CopyFilesProps } from './DnD.types'
import Button from '../button/Button'
import Icon from '../icon/Icon'
import './dnd.scss'

const DnD = ({ maxFiles = 1, maxFileSize, copy, onChange }: DnDProps) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [files, setFiles] = useState<any>([])
  const [isDragged, setDragged] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isError, setIsError] = useState(false)
  const numFiles = files.length
  const isExceeded = !(numFiles >= maxFiles)

  const triggerInput = (e: any) => {
    e.persist()
    if (
      inputRef.current &&
      !e.currentTarget.hasAttribute('disabled') &&
      !isError
    ) {
      inputRef.current.click()
    }
  }

  useEffect(() => {
    onChangeList(files)
    onChange && onChange(files)
    // eslint-disable-next-line
  }, [files])

  const onChangeList = (files: FileProps[]) => {
    setIsLoading(false)
    setIsError(false)

    files.forEach((file: FileProps) => {
      if (file.loading) setIsLoading(true)
      if (file.error) setIsError(true)
    })
  }

  const deleteFile = (e: any) => {
    e.stopPropagation()
    e.preventDefault()

    const id = e.target.closest('.DnD__file--trash').getAttribute('data-delete')
    const updateFiles = [...files].filter((file: any) => file.id !== id)

    setFiles([...updateFiles])
  }

  const validateSize = (file: any, sizeLimit: number) =>
    file.size / (1000 * 1000) > sizeLimit

  const uploadFile = (file: any) => {
    let errorMaxSize = false
    let errorMessage = ''

    if (validateSize(file, maxFileSize ?? 1)) {
      errorMaxSize = true
      errorMessage = 'Excede el tamaño permitido'
    }

    const reader = new FileReader()
    const id = Math.random().toString(16).slice(2)

    reader.onload = (readerResponse: any) => {
      const newFile = {
        result: readerResponse.target.result,
        file,
        id,
        loading: false,
        error: errorMaxSize,
        errorMessage: errorMessage,
      }
      setFiles([...files, newFile])
    }

    reader.readAsArrayBuffer(file)
  }

  const onChangeInput = (e: any) => {
    if (isExceeded) {
      const file = e.target.files[0]

      if (inputRef.current && !!file) {
        uploadFile(file)
        inputRef.current.value = ''
      }
    }
  }

  const onDragOver = (e: any) => {
    if ('preventDefault' in e) {
      e.stopPropagation()
      e.preventDefault()
    }

    if (e.type === 'dragover') {
      setDragged(true)
    } else {
      setDragged(false)
    }
  }

  const onFileSelect = (e: any) => {
    if (isExceeded) {
      onDragOver(e)
      setDragged(false)

      const files = e.dataTransfer.files
      for (const file in files) {
        if (!!files[file] && !(file === 'length' || file === 'item')) {
          uploadFile(files[file])
        }
      }
    }
  }

  return (
    <div
      className='DnD__container flex flex-col items-center justify-center w-full h-full bg-transparent'
      style={{
        backgroundColor: isDragged ? '#cbdfff' : 'transparent',
        borderColor: isDragged ? '#0661fc' : '#79959f',
      }}
      onClick={triggerInput}
      onDragOver={onDragOver}
      onDragLeave={onDragOver}
      onDrop={onFileSelect}
      data-testid='DnD__container'
    >
      <CopyFiles
        maxFiles={maxFiles}
        numFiles={numFiles}
        isLoading={isLoading}
        isError={isError}
      />
      <input
        type='file'
        className='DnD__input'
        data-testid='DnD__input'
        ref={inputRef}
        onChange={onChangeInput}
      />
      {numFiles > 0 && <File files={files} deleteFile={deleteFile} />}
      {isExceeded && (
        <Button
          type='clear'
          text='Subir archivo'
          size='md'
          iconName='download'
          disabled={isError}
        />
      )}
      <span className='DnD__copy' data-testid='DnD__copy'>
        {copy}
      </span>
    </div>
  )
}

const CopyFiles = ({
  maxFiles = 1,
  numFiles = 1,
  isLoading,
  isError,
}: CopyFilesProps) => {
  return (
    <p className='DnD__text' data-testid='DnD__text'>
      {isLoading ? (
        'Estamos subiendo tu(s) archivos(s)'
      ) : isError ? (
        'Ha ocurrido un error'
      ) : numFiles === 0 || maxFiles === numFiles ? (
        `Máximo ${maxFiles} archivos`
      ) : (
        <>
          Hemos subido tu(s) archivos(s)
          <br />
          puedes subir {maxFiles - numFiles} más
        </>
      )}
    </p>
  )
}

const File = ({ files, deleteFile }: any) => {
  return (
    <div className='DnD__files'>
      {files.map((file: any, index: number) => {
        const size = file.file.size / (1000 * 1000)
        const success = !file.loading && !file.error ? 'DnD__file--success' : ''
        const loading =
          !!file.loading && !file.error ? 'DnD__file--loading' : ''
        const error = !!file.error ? 'DnD__file--error' : ''

        return (
          <div
            className='DnD__file--container'
            data-testid='DnD__file--container'
            key={`DnD__file--container-${index}`}
          >
            <div
              className={[
                'flex items-center justify-center',
                success,
                loading,
                error,
              ].join(' ')}
            >
              <div className='DnD__file'>
                <p className='flex'>
                  <Icon iconName='file' size={13} />
                  <span className='DnD__file--name'>{file.file.name}</span>
                </p>
                {size < 1 ? size.toFixed(1) : Math.round(size)}MB
              </div>
              <div
                className='DnD__file--trash'
                data-delete={file.id}
                onClick={(e) => {
                  deleteFile(e)
                }}
              >
                <Icon className='p-xsm' iconName='trash-2' size={18} />
              </div>
            </div>
            {!!file.error && (
              <div className='flex text-error mt-xsm text-small-02'>
                <Icon
                  className='text-error mr-xsm cursor-pointer'
                  iconName='alert-triangle'
                  size={13}
                />
                {!file.errorMessage ? 'Error' : file.errorMessage}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

DnD.propTypes = {
  /**
   * Max files of dnd
   */
  maxFiles: PropTypes.number,
  /**
   * maximum file size of dnd
   */
  maxFileSize: PropTypes.number,
  /**
   * DnD copy
   */
  copy: PropTypes.object,
  /**
   * Classes of dnd
   */
  className: PropTypes.string,
  /**
   * onChange of dnd
   */
  onChange: PropTypes.func,
}

DnD.defaultProps = {
  maxFileSize: 30,
  className: '',
  copy: (
    <p>
      Archivos PDF, PPT, Word, Excel o JPG
      <br />
      (Tamaño máximo por cada archivo 30 MB)
    </p>
  ),
  maxFiles: 8,
  onChange: () => {},
}

export default DnD
